/**
 * A geometria do editor de foto do Ranking.
 *
 * A foto de cada vendedor é guardada num padrão só: quadrada, `LADO_DA_FOTO` de lado, com três
 * pontos marcados — os dois olhos e a boca. É disso que as cenas precisam para desenhar a expressão
 * por cima do rosto. O editor leva qualquer imagem até esse padrão com quatro controles (arrastar,
 * zoom, giro e os três pontos), e a conta de cada um mora aqui, separada da tela, para ser testada.
 *
 * Dois sistemas de coordenadas, ambos em FRAÇÃO (0 a 1), que é como a API guarda:
 *
 * - **quadro**: a foto final, já recortada. É o que vai para o servidor;
 * - **imagem**: o arquivo original. Os pontos ficam guardados NELA enquanto se edita, para
 *   acompanharem o rosto quando alguém mexe no zoom depois de já tê-los marcado.
 */

export const LADO_DA_FOTO = 512;
export const ZOOM_MAXIMO = 4;
// Graus, para cada lado: a volta inteira. Começou em 30, só para nivelar cabeça inclinada, mas foto
// de celular chega deitada ou de ponta-cabeça, e aí não havia como endireitar sem outro programa.
export const GIRO_MAXIMO = 180;
// O controle deslizante cobre só o ajuste fino; os quartos de volta são do botão. Meia volta
// espalhada num controle de 360 px daria um grau por pixel, e nivelar um rosto pede menos que isso.
export const GIRO_FINO_MAXIMO = 45;

/** Enquadramento típico de foto de perfil. É de onde os pontos partem, e o que a cena usa sem eles. */
export const PONTOS_PADRAO = { olhos: [[0.36, 0.42], [0.64, 0.42]], boca: [0.5, 0.7] };

export const RECORTE_INICIAL = { zoom: 1, giro: 0, dx: 0, dy: 0 };

const radianos = (graus) => (graus * Math.PI) / 180;
const limitar = (valor, minimo, maximo) => Math.max(minimo, Math.min(maximo, valor));

/**
 * Quantos pixels do quadro cada pixel da imagem ocupa.
 *
 * Com zoom 1 a imagem COBRE o quadro pelo lado menor. O giro aumenta a escala sozinho: um quadrado
 * girado deixa os cantos do quadro descobertos, e a alternativa seria salvar foto com canto vazio.
 */
export const escalaDoRecorte = (imagem, recorte) => {
  const angulo = radianos(recorte.giro);

  return (LADO_DA_FOTO / Math.min(imagem.largura, imagem.altura))
    * recorte.zoom
    * (Math.abs(Math.cos(angulo)) + Math.abs(Math.sin(angulo)));
};

/**
 * Corrige o recorte para a imagem nunca deixar um pedaço do quadro vazio.
 *
 * O deslocamento é limitado nos eixos da PRÓPRIA imagem (já girada): é ali que a folga é um
 * retângulo simples — metade da imagem menos a metade do quadro girado.
 */
export const ajustarRecorte = (imagem, recorte) => {
  const zoom = limitar(Number(recorte.zoom) || 1, 1, ZOOM_MAXIMO);
  const giro = limitar(Number(recorte.giro) || 0, -GIRO_MAXIMO, GIRO_MAXIMO);
  const angulo = radianos(giro);
  const cos = Math.cos(angulo);
  const sen = Math.sin(angulo);
  const escala = escalaDoRecorte(imagem, { zoom, giro });
  const meioQuadro = (LADO_DA_FOTO / 2) * (Math.abs(cos) + Math.abs(sen));
  const folgaX = Math.max(0, (imagem.largura * escala) / 2 - meioQuadro);
  const folgaY = Math.max(0, (imagem.altura * escala) / 2 - meioQuadro);

  const x = (Number(recorte.dx) || 0) * LADO_DA_FOTO;
  const y = (Number(recorte.dy) || 0) * LADO_DA_FOTO;
  // Para os eixos da imagem, limita, e volta.
  const naImagemX = limitar(x * cos + y * sen, -folgaX, folgaX);
  const naImagemY = limitar(-x * sen + y * cos, -folgaY, folgaY);

  return {
    zoom,
    giro,
    dx: (naImagemX * cos - naImagemY * sen) / LADO_DA_FOTO,
    dy: (naImagemX * sen + naImagemY * cos) / LADO_DA_FOTO,
  };
};

/** Um ponto da imagem (fração dela) → onde ele cai no quadro (fração do quadro). */
export const daImagemParaOQuadro = ([px, py], imagem, recorte) => {
  const angulo = radianos(recorte.giro);
  const escala = escalaDoRecorte(imagem, recorte);
  const x = (px - 0.5) * imagem.largura * escala;
  const y = (py - 0.5) * imagem.altura * escala;

  return [
    0.5 + recorte.dx + (x * Math.cos(angulo) - y * Math.sin(angulo)) / LADO_DA_FOTO,
    0.5 + recorte.dy + (x * Math.sin(angulo) + y * Math.cos(angulo)) / LADO_DA_FOTO,
  ];
};

/** O inverso: um ponto do quadro → em que lugar da imagem ele está. */
export const doQuadroParaAImagem = ([qx, qy], imagem, recorte) => {
  const angulo = radianos(recorte.giro);
  const escala = escalaDoRecorte(imagem, recorte);
  const x = (qx - 0.5 - recorte.dx) * LADO_DA_FOTO;
  const y = (qy - 0.5 - recorte.dy) * LADO_DA_FOTO;

  return [
    0.5 + (x * Math.cos(angulo) + y * Math.sin(angulo)) / (imagem.largura * escala),
    0.5 + (-x * Math.sin(angulo) + y * Math.cos(angulo)) / (imagem.altura * escala),
  ];
};

const cadaPonto = (pontos, converter) => ({
  olhos: pontos.olhos.map(converter),
  boca: converter(pontos.boca),
});

export const pontosNoQuadro = (pontosNaImagem, imagem, recorte) => cadaPonto(
  pontosNaImagem,
  (ponto) => daImagemParaOQuadro(ponto, imagem, recorte),
);

export const pontosNaImagem = (pontosDoQuadro, imagem, recorte) => cadaPonto(
  pontosDoQuadro,
  (ponto) => doQuadroParaAImagem(ponto, imagem, recorte),
);

/**
 * Os pontos como a API aceita: dentro da foto, com quatro casas.
 *
 * Um ponto arrastado para fora do recorte é trazido para a borda — o servidor recusa o que estiver
 * fora de 0 a 1, e perder a foto inteira por um olho na margem seria pior que a borda.
 */
export const pontosParaSalvar = (pontosDoQuadro) => cadaPonto(
  pontosDoQuadro,
  ([x, y]) => [limitar(x, 0, 1), limitar(y, 0, 1)].map((valor) => Math.round(valor * 10000) / 10000),
);

/**
 * O giro que deixa os dois olhos na mesma altura.
 *
 * Cabeça inclinada na foto vira olho de desenho torto na cena. Em vez de pedir que a pessoa acerte
 * o controle de giro no olho, ela marca os dois olhos e o editor nivela.
 */
export const giroQueNivelaOsOlhos = (pontosDoQuadro, giroAtual = 0) => {
  const [[x1, y1], [x2, y2]] = pontosDoQuadro.olhos;
  // Da esquerda para a direita, qualquer que seja a ordem em que foram marcados.
  const inclinacao = x2 >= x1
    ? Math.atan2(y2 - y1, x2 - x1)
    : Math.atan2(y1 - y2, x1 - x2);

  // Dá a volta em vez de travar na borda: de ponta-cabeça, nivelar pode passar de 180°.
  return normalizarGiro(giroAtual - (inclinacao * 180) / Math.PI);
};

/**
 * O giro em duas partes: quartos de volta (o botão) e o ajuste fino em torno deles (o controle).
 *
 * `fino` fica sempre entre -45 e 45, e `quartos * 90 + fino` devolve o giro.
 */
export const partesDoGiro = (giro) => {
  const quartos = Math.round((Number(giro) || 0) / 90);

  return { quartos, fino: (Number(giro) || 0) - quartos * 90 };
};

/** Traz qualquer ângulo para a faixa do editor: 190° é o mesmo que -170°. */
export const normalizarGiro = (graus) => {
  const volta = (((Number(graus) || 0) + 180) % 360 + 360) % 360 - 180;

  return volta === -180 ? 180 : volta;
};

/** Mais um quarto de volta no sentido horário, mantendo o ajuste fino. */
export const girarUmQuarto = (giro) => normalizarGiro((Number(giro) || 0) + 90);

/** Se a foto pode ser salva como está, sem regravar a imagem — só os pontos mudaram. */
export const recorteIntocado = (recorte) => recorte.zoom === 1
  && recorte.giro === 0
  && Math.abs(recorte.dx) < 1e-6
  && Math.abs(recorte.dy) < 1e-6;

/** Desenha o recorte num contexto 2D de `LADO_DA_FOTO` de lado. */
export const desenharRecorte = (contexto, elemento, imagem, recorte) => {
  const escala = escalaDoRecorte(imagem, recorte);

  contexto.save();
  contexto.setTransform(1, 0, 0, 1, 0, 0);
  contexto.fillStyle = '#F3E8FF';
  contexto.fillRect(0, 0, LADO_DA_FOTO, LADO_DA_FOTO);
  contexto.translate(LADO_DA_FOTO * (0.5 + recorte.dx), LADO_DA_FOTO * (0.5 + recorte.dy));
  contexto.rotate(radianos(recorte.giro));
  contexto.scale(escala, escala);
  contexto.imageSmoothingQuality = 'high';
  contexto.drawImage(elemento, -imagem.largura / 2, -imagem.altura / 2, imagem.largura, imagem.altura);
  contexto.restore();
};
